import { Box, Flex } from '@chakra-ui/layout';
import { Tag, TagLabel } from '@chakra-ui/react';
import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { CreditAgreement } from '@finley-technologies/finley-types';

import { TAccountTypeGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { EditableTitle } from '../../../../../common/Inputs';
import { getUnusedFeeFormValues } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/expense-mechanics/[expenseId]/dataMappings/getFormValues';
import { setFormValues } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/expense-mechanics/[expenseId]/dataMappings/setFormValues';
import {
  useExpenseDiffResult,
  useGetCapitalProviderOptionsByTAccount,
  useGetLiabilityAccountsByTAccount,
} from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/expense-mechanics/[expenseId]/hooks';
import { AutoSave } from '~/core/routes/settings/implementations/common/AutoSave';
import {
  SelectInput,
  TextInput,
  WholePageDiffOutline,
} from '~/core/routes/settings/implementations/common/DiffInputs';
import { DiffableFormData } from '~/core/routes/settings/implementations/common/types';

export type BaseUnusedFeeFormData = {
  capitalProviderSlug: string;
  name: string;
  value: number | null;
  type: CreditAgreement.DomainEntity.LiabilityAccountProperty['type'];
  tAccount: Extract<TAccountTypeGQLType, 'unusedInterestExpense'>;
};

type UnusedFeeFormData = DiffableFormData<BaseUnusedFeeFormData>;

export const UnusedFee = ({
  counterpartySlug,
}: {
  counterpartySlug: string;
}) => {
  const unusedFeeLiabilityAccounts = useGetLiabilityAccountsByTAccount(
    'unusedInterestExpense',
  );
  const capitalProviderOptions = useGetCapitalProviderOptionsByTAccount(
    counterpartySlug,
    'unusedInterestExpense',
  );

  const defaultValues = useMemo(() => {
    return getUnusedFeeFormValues(counterpartySlug, unusedFeeLiabilityAccounts);
  }, [counterpartySlug, unusedFeeLiabilityAccounts]);

  const form = useForm<UnusedFeeFormData>({
    defaultValues: { wipForm: defaultValues },
    mode: 'onTouched',
  });

  const diffResult = useExpenseDiffResult(
    counterpartySlug,
    'unusedInterestExpense',
  );

  useEffect(() => {
    form.setValue('diffForm', diffResult?.previousExpenseFormData);
  }, [form, diffResult]);

  return (
    <FormProvider {...form}>
      {/* // todo @implementation @markhaghani - use zustand state mgmt to prevent inadvertent adding of unused fee on back navigation */}
      <AutoSave<UnusedFeeFormData> onSave={setFormValues} />
      <WholePageDiffOutline diff={diffResult?.diffResult}>
        <form>
          <Flex gap="1rem" direction="column">
            <Flex gap="1.5rem" alignItems="center">
              <Box flexShrink="0">
                <EditableTitle<UnusedFeeFormData>
                  name="wipForm.name"
                  required
                />
              </Box>
              <Tag variant="solid-muted" colorScheme="blue">
                <TagLabel>Unused Fee</TagLabel>
              </Tag>
            </Flex>
            <SelectInput<UnusedFeeFormData>
              name="wipForm.capitalProviderSlug"
              diffName="diffForm.capitalProviderSlug"
              label="Capital Provider"
              isDisabled // todo @implementation @markhaghani - don't disable the fee if it's not being used
              placeholder="Select Capital Provider"
              options={capitalProviderOptions}
            />
            <Flex
              sx={{
                border: '1px solid',
                borderColor: 'gray.200',
                borderRadius: 'md',
                flexDirection: 'column',
                gap: '1rem',
                p: '1rem',
              }}
            >
              <SelectInput<UnusedFeeFormData>
                name="wipForm.type"
                diffName="diffForm.type"
                label="Type"
                isDisabled
                placeholder="Fixed"
                options={[{ label: 'Fixed', value: 'fixed' }]}
              />
              <TextInput<UnusedFeeFormData>
                name="wipForm.value"
                diffName="diffForm.value"
                label="Value"
                placeholder="%"
                mask="percentage"
                helperText="Specifies the fixed rate used to determine the unused fee for the
                facility. This rate is applied against the unused balance of the
                facility to calculate the unused fee."
                required
              />
            </Flex>
            <Flex />
          </Flex>
        </form>
      </WholePageDiffOutline>
    </FormProvider>
  );
};
